/**
 * TapRouterPage.jsx — the icon grid a customer sees after scanning
 * a HustleTap NFC tag or QR code.
 *
 * Routes:
 *   /t/:tag_id        — real venue (fetches from /api/tapnfc/venue/:tag_id)
 *   /t/demo           — sample "Bella Cucina" venue for showcase
 *
 * Also mounted at the ROOT of demo.hustletap.au / demo.tapnfc.au /
 * .com.au variants via hostRegistry → "tapnfc-demo" mode.
 *
 * Design: bright-orange grid of 6-12 circular icon buttons. Every
 * icon has data-testid + accessible label. Wi-Fi icon uses the
 * standard `WIFI:T:...;S:...;P:...;;` URI so iOS/Android show a
 * native "Join this network?" prompt on tap.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const ORANGE = "#ff6a13";
const ORANGE_DEEP = "#e04c00";
const CREAM = "#fff7ec";
const INK = "#0f0a05";

// ─────────────────────────────────────────────────────────────
// Inline SVG icon library — one shape per `kind`.
// Keeps the shell < 15KB total, no font-icon CDN.
// ─────────────────────────────────────────────────────────────
const ICONS = {
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 3h12v18H6z" /><path d="M10 7h4M10 11h4M10 15h4" />
    </svg>
  ),
  order: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  google_review: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2L14.6 8.2 21.3 8.8 16.2 13.1 17.9 19.7 12 16.3 6.1 19.7 7.8 13.1 2.7 8.8 9.4 8.2z" />
    </svg>
  ),
  tripadvisor_review: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="12" r="4" /><circle cx="16" cy="12" r="4" /><circle cx="8" cy="12" r="1.2" fill="currentColor" /><circle cx="16" cy="12" r="1.2" fill="currentColor" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  wifi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.55a11 11 0 0 1 14 0" /><path d="M1.42 9a16 16 0 0 1 21.16 0" /><path d="M8.53 16.11a6 6 0 0 1 6.95 0" /><line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  ),
  contact: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  gallery: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  offers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 12l-1.5-1.5V8a2 2 0 0 0-2-2h-2.5L12.5 4.5 11 6H8.5a2 2 0 0 0-2 2v2.5L5 12l1.5 1.5V16a2 2 0 0 0 2 2H11l1.5 1.5L14 18h2.5a2 2 0 0 0 2-2v-2.5z" /><circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 12V8a2 2 0 0 0-2-2H5a2 2 0 0 1 0-4h13a4 4 0 0 1 4 4v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4" /><path d="M16 12h4" />
    </svg>
  ),
  custom: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────────
// Icon button
// ─────────────────────────────────────────────────────────────
function IconButton({ icon, accent }) {
  const shape = ICONS[icon.kind] || ICONS.custom;
  // Wi-Fi URIs (WIFI:…) don't open in a WKWebView tap the same way an
  // https:// link does — on iOS we show a toast with the credentials
  // + a copy button. On Android the WIFI: URI works natively.
  const isWifi = icon.kind === "wifi" && String(icon.url || "").startsWith("WIFI:");
  const isVCard = icon.kind === "contact" && String(icon.url || "").endsWith(".vcf");

  return (
    <a href={icon.url} target={isWifi ? undefined : "_blank"} rel="noreferrer"
       data-testid={`tap-icon-${icon.kind}`}
       aria-label={icon.label}
       download={isVCard ? "contact.vcf" : undefined}
       style={{
         display: "flex", flexDirection: "column", alignItems: "center",
         textDecoration: "none", color: INK, gap: 8,
         padding: 14, borderRadius: 20,
         background: "#fff", border: `1px solid ${accent}22`,
         boxShadow: "0 8px 20px rgba(15,10,5,0.06)",
         transition: "transform .15s ease, box-shadow .15s ease",
       }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: `linear-gradient(135deg, ${accent} 0%, ${ORANGE_DEEP} 100%)`,
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 10px 22px ${accent}55`,
      }}>
        <div style={{ width: 30, height: 30 }}>{shape}</div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>{icon.label}</span>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────
export default function TapRouterPage({ forceTagId }) {
  const { tag_id } = useParams();
  const effective = forceTagId || tag_id || "demo";
  const [venue, setVenue] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = effective === "demo"
          ? `${API}/api/tapnfc/demo/venue`
          : `${API}/api/tapnfc/venue/${encodeURIComponent(effective)}`;
        const { data } = await axios.get(url);
        if (alive) setVenue(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { alive = false; };
  }, [effective]);

  const accent = (venue && venue.accent_hex) || ORANGE;

  if (error) {
    return (
      <div style={{
        minHeight: "100vh", background: CREAM, color: INK,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
      }} data-testid="tap-router-error">
        <div style={{ textAlign: "center", padding: 32, maxWidth: 340 }}>
          <div style={{ fontSize: 48 }}>⚠</div>
          <p style={{ fontSize: 18, fontWeight: 700 }}>{error}</p>
          <a href="https://hustletap.au" style={{ color: ORANGE_DEEP, fontWeight: 700 }}>What is HustleTap?</a>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div style={{
        minHeight: "100vh", background: CREAM,
        display: "flex", alignItems: "center", justifyContent: "center",
      }} data-testid="tap-router-loading">
        <div style={{ fontSize: 14, color: "#6d5a44" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="tap-router-page" style={{
      minHeight: "100vh", background: CREAM, color: INK,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
      display: "flex", flexDirection: "column",
    }}>
      {/* Hero */}
      <div style={{
        background: `linear-gradient(180deg, ${accent} 0%, ${ORANGE_DEEP} 100%)`,
        color: "#fff", padding: "32px 24px 40px", textAlign: "center",
        borderRadius: "0 0 32px 32px",
      }}>
        {venue.logo_url && (
          <img src={venue.logo_url} alt={`${venue.business_name} logo`}
               style={{ width: 76, height: 76, borderRadius: 22, objectFit: "cover",
                        marginBottom: 14, boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                        border: "3px solid rgba(255,255,255,0.5)" }}
               data-testid="tap-router-logo" />
        )}
        <h1 style={{
          margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }} data-testid="tap-router-business-name">{venue.business_name}</h1>
        {venue.city && (
          <p style={{ margin: "8px 0 0", fontSize: 13, opacity: 0.85, fontWeight: 600 }}>
            {venue.city}
          </p>
        )}
        {venue.dormant && (
          <div style={{
            marginTop: 14, display: "inline-block", padding: "6px 14px",
            background: "rgba(0,0,0,0.25)", borderRadius: 999,
            fontSize: 11, fontWeight: 800, letterSpacing: "0.06em",
          }} data-testid="tap-router-dormant-pill">TAG NOT YET ACTIVATED</div>
        )}
      </div>

      {/* Icon grid */}
      <div style={{
        maxWidth: 480, margin: "-20px auto 0",
        padding: "0 20px 40px", flex: 1, width: "100%",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
          gap: 12,
        }} data-testid="tap-router-icon-grid">
          {(venue.icons || []).map((i, idx) => (
            <IconButton key={i.id || idx} icon={i} accent={accent} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        background: INK, color: "#a8927a", padding: "18px 24px",
        fontSize: 12, textAlign: "center",
      }}>
        Powered by <a href="https://hustletap.au" style={{ color: accent, fontWeight: 700, textDecoration: "none" }}>HustleTap</a>
      </div>
    </div>
  );
}
